/*
PROV: COHINT001.BF.GNEPPK5PYY
REQ: SYS-ARCH-15
WHY: Shared package module.
*/

import type { NextRouter } from "next/router";

import { getLastApiContext } from "../api/client";

type RumCommon = {
  app: string;
  app_env?: string;
  git_sha?: string;
  build_timestamp?: string;
  last_request_id?: string;
  last_correlation_id?: string;
  last_trace_id?: string;
  at_utc: string;
};

type RumWebVitalEvent = RumCommon & {
  type: "web_vital";
  metric_name: string;
  metric_value: number;
  metric_id?: string;
  metric_start_time?: number;
  metric_label?: string;
  page?: string;
};

type RumRouteTimingEvent = RumCommon & {
  type: "route_timing";
  from_page?: string;
  to_page?: string;
  duration_ms: number;
  ok: boolean;
};

type RumEvent = RumWebVitalEvent | RumRouteTimingEvent;

export type RumOptions = {
  app: string;
  endpoint?: string;
  enabled?: boolean;
  sampleRate?: number;
};

function nowIso(): string {
  try {
    return new Date().toISOString();
  } catch {
    return "";
  }
}

function pickEnv(key: string): string | undefined {
  const v = (process as any)?.env?.[key];
  if (!v) return undefined;
  return String(v);
}

function parseBool(v: unknown): boolean {
  const raw = String(v ?? "").trim().toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes";
}

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function getEnabled(options?: RumOptions): boolean {
  if (options?.enabled != null) return Boolean(options.enabled);
  return parseBool(pickEnv("NEXT_PUBLIC_RUM_ENABLED"));
}

function getSampleRate(options?: RumOptions): number {
  if (options?.sampleRate != null) return clamp(Number(options.sampleRate), 0, 1);
  const raw = pickEnv("NEXT_PUBLIC_RUM_SAMPLE_RATE");
  if (!raw) return 0;
  return clamp(Number(raw), 0, 1);
}

function shouldSample(sampleRate: number): boolean {
  if (sampleRate <= 0) return false;
  if (sampleRate >= 1) return true;
  return Math.random() < sampleRate;
}

function safeNextPageRoute(): string | undefined {
  try {
    const anyWindow = window as any;
    const page = anyWindow?.__NEXT_DATA__?.page;
    if (typeof page !== "string") return undefined;
    const trimmed = page.trim();
    if (!trimmed || trimmed.length > 200) return undefined;
    return trimmed;
  } catch {
    return undefined;
  }
}

function buildCommon(app: string): RumCommon {
  const ctx = getLastApiContext();
  return {
    app,
    app_env: pickEnv("NEXT_PUBLIC_APP_ENV"),
    git_sha: pickEnv("NEXT_PUBLIC_GIT_SHA"),
    build_timestamp: pickEnv("NEXT_PUBLIC_BUILD_TIMESTAMP"),
    last_request_id: ctx.requestId,
    last_correlation_id: ctx.correlationId,
    last_trace_id: ctx.traceId,
    at_utc: nowIso(),
  };
}

async function sendEvent(endpoint: string, payload: RumEvent): Promise<void> {
  try {
    await fetch(endpoint, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-correlation-id": (globalThis as any)?.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
      },
      body: JSON.stringify(payload),
      credentials: "include",
      keepalive: true,
    });
  } catch {
    // Intentionally swallow.
  }
}

export async function reportWebVital(metric: any, options: RumOptions): Promise<void> {
  if (typeof window === "undefined") return;

  const enabled = getEnabled(options);
  if (!enabled) return;

  const sampleRate = getSampleRate(options);
  if (!shouldSample(sampleRate)) return;

  const endpoint = options.endpoint || "/api/v1/telemetry/web-vitals";
  const name = String(metric?.name ?? "").trim();
  if (!name) return;

  const value = Number(metric?.value);
  if (!Number.isFinite(value)) return;

  const payload: RumWebVitalEvent = {
    ...buildCommon(options.app),
    type: "web_vital",
    metric_name: name,
    metric_value: Math.round(value * 1000) / 1000,
    metric_id: typeof metric?.id === "string" ? metric.id.slice(0, 80) : undefined,
    metric_start_time: Number.isFinite(Number(metric?.startTime)) ? Math.round(Number(metric.startTime)) : undefined,
    metric_label: typeof metric?.label === "string" ? metric.label.slice(0, 40) : undefined,
    page: safeNextPageRoute(),
  };

  await sendEvent(endpoint, payload);
}

export function installNextRouteTimingReporter(router: NextRouter, options: RumOptions): () => void {
  if (typeof window === "undefined") return () => {};
  const enabled = getEnabled(options);
  if (!enabled) return () => {};

  const endpoint = options.endpoint || "/api/v1/telemetry/route-timing";
  const sampleRate = getSampleRate(options);

  let startAt: number | null = null;
  let fromPage: string | undefined;

  const onStart = () => {
    startAt = Date.now();
    fromPage = String(router.pathname || "").trim() || undefined;
  };

  const emit = async (ok: boolean) => {
    if (startAt == null) return;
    const durationMs = Date.now() - startAt;
    startAt = null;

    if (!shouldSample(sampleRate)) return;

    const payload: RumRouteTimingEvent = {
      ...buildCommon(options.app),
      type: "route_timing",
      from_page: fromPage,
      to_page: String(router.pathname || "").trim() || undefined,
      duration_ms: Math.max(0, Math.min(120_000, Math.round(durationMs))),
      ok,
    };
    await sendEvent(endpoint, payload);
  };

  const onComplete = () => {
    void emit(true);
  };

  const onError = () => {
    void emit(false);
  };

  router.events.on("routeChangeStart", onStart);
  router.events.on("routeChangeComplete", onComplete);
  router.events.on("routeChangeError", onError);

  return () => {
    router.events.off("routeChangeStart", onStart);
    router.events.off("routeChangeComplete", onComplete);
    router.events.off("routeChangeError", onError);
  };
}
